import {
  ComboBox,
  IComboBoxStyles,
  Label,
  Spinner
} from '@fluentui/react';
import {FC, useState, useEffect} from 'react';

import styles from './AcknowledgementTemplatePicker.module.scss';
import {TheButton} from '../../../common/TheButton';
import {ConfirmationTemplateModal} from './ConfirmationTemplateModal';
import {DocumentSystem} from '../../../../models/DocumentSystem';
import {AcknowledgementsProcess} from '../../../../models/AcknowledgementsProcess';
import {useAcknowledgementTemplate} from '../../../../hooks/services/useAcknowledgementTemplate';
import {AcknowledgementTemplateDto} from '../../../../services/generated/models/AcknowledgementTemplateDto';
import { ModalWithHeader } from '../../../common/ModalWithHeader';
import { useAcknowledgementTemplateService } from './useAcknowledgementTemplateService';

interface IAcknowledgementTemplatePickerProps {
  unitId: number;
  documents: DocumentSystem[] | [];
  processes: AcknowledgementsProcess[] | [];
  reason: string;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  templateTitle: string | undefined;
  isTemplateModalVisible: boolean;
  setIsTemplateModalVisible: (visible: boolean) => void;
  selectedKey: number;
  setSelectedKey: (key: number) => void;
  openConfirmationTemplateModal: () => void;
}

export const AcknowledgementTemplatePicker: FC<IAcknowledgementTemplatePickerProps> = ({
  unitId,
  documents,
  processes,
  reason,
  isLoading,
  setIsLoading,
  templateTitle,
  isTemplateModalVisible,
  setIsTemplateModalVisible,
  selectedKey,
  setSelectedKey,
  openConfirmationTemplateModal
}) => {

  const comboBoxStyles: Partial<IComboBoxStyles> = {
  root: {
    width: '100%',
  },
  callout: {
    width: '29%', 
  },
  };

  const [title, setTitle] = useState<string | undefined>(templateTitle);
  const [isDeleteModalVisibile, setIsDeleteModalVisible] = useState<boolean>(false);
  const [isAdd, setIsAdd] = useState<boolean>(false);
  const {createTemplate,updateTemplate, deleteTemplate} = useAcknowledgementTemplate();
  const {refreshData, options, unitsTemplates} = useAcknowledgementTemplateService();


   useEffect(() => {
    if(!unitId || unitId === 0)
    {
      return
    }
    refreshData(unitId)
  }, [refreshData, unitId]);

  useEffect(() => {
    const selectedTemplate = unitsTemplates.find(item => item.id === selectedKey);
    setTitle(selectedTemplate ? selectedTemplate.title : '');
  },[selectedKey, unitsTemplates, isAdd]);


  const onFormSave = async (formData: AcknowledgementTemplateDto) => {
    setIsTemplateModalVisible(false);
    setIsLoading(true);
    const response = await createTemplate(formData);
    if(response && response.result)
    {
      setSelectedKey(response.result.id);
    }
    refreshData(unitId);
    setIsLoading(false);

  };

    const onFormUpdate = async (formData: AcknowledgementTemplateDto) => {
    setIsTemplateModalVisible(false);
    setIsLoading(true);
    await updateTemplate(formData);
    refreshData(unitId);
    setIsLoading(false);

  };

  const onDeleteForm = async () => {
    setIsDeleteModalVisible(false);
    setIsLoading(true);
    await deleteTemplate(unitId, selectedKey);
    setSelectedKey(-1);
    refreshData(unitId);
    setTitle(undefined);
    setIsLoading(false);
  };

  const onConfirmForm = async () => {
    const documentsToSend = documents
      .filter(item => item.checkedAcknowledgementItem === true)
      .map(item => item.id);
    const processesToSend = processes
      .filter(item => item.checkedAcknowledgementItem === true)
      .map(item => item.id);
    if (!reason || (!documentsToSend && !processesToSend)) {
      return;
    }

    const formData = {
      id: selectedKey,
      documentIds: documentsToSend,
      processIds: processesToSend,
      reason: reason,
      title: title,
      unitId: unitId
    };

    if(isAdd)
    {
      await onFormSave(formData);
    }
    else
    {
      
     await onFormUpdate(formData)
    }
  };


  return !options  ? (
    <Spinner />
  ) : (
    <>
      <div className={styles.acknowledgementTemplateContainer}>
        {isLoading ? <div className={styles.spinnerWrapper}><Spinner /></div> :
        <ComboBox
          disabled={isLoading}
          className={styles.comboBox}
          allowFreeform={true}
          autoComplete={'on'}
          styles={comboBoxStyles}
          options={options}
           onChange={(_, option) => {
                    if (option) 
                      {
                        setSelectedKey(option.key as number)
                      };
                  }}
          selectedKey={selectedKey}
          placeholder="Template"
        />}
        <TheButton
          primary
          disabled={isLoading}
          iconProps={{iconName: 'Add'}}
          onClick={() => { 
            setIsAdd(true);
            openConfirmationTemplateModal()}
            }>
          Add New
        </TheButton>
        <TheButton
          primary
          disabled={selectedKey === -1 || isLoading}
          iconProps={{iconName: 'Edit'}}
         onClick={() => {
           setIsAdd(false);
           openConfirmationTemplateModal()}
         }>
          Update Selected
        </TheButton>
        <TheButton
          primary
          disabled={selectedKey === -1 || isLoading}
          iconProps={{iconName: 'Trash'}}
          onClick={() => setIsDeleteModalVisible(true)}>
          Delete Selected
        </TheButton>
      </div>
      <ConfirmationTemplateModal
        title={title}
        setTitle={setTitle}
        isAdd={isAdd}
        isVisible={isTemplateModalVisible}
        setIsVisible={setIsTemplateModalVisible}
        onConfirmForm={onConfirmForm}
        reason={reason || ''}
        documents={documents.filter(item => item.checkedAcknowledgementItem)}
        processes={processes.filter(item => item.checkedAcknowledgementItem)}
      />
      <ModalWithHeader
        header="Are you sure you want to delete this template?"
        isVisible={isDeleteModalVisibile}
        dismiss={() => setIsDeleteModalVisible(false)}>
         <Label>{title}</Label>
          <div className={styles.buttons}>
          <TheButton primary onClick={() => setIsDeleteModalVisible(false)}>
            Cancel
          </TheButton>

          <TheButton primary onClick={() => onDeleteForm()}>
            Confirm
          </TheButton>
        </div>
       </ModalWithHeader>

    </>
  );
};
